// keyrd_console/src/components/BehaviorHistory.js
import React, { useEffect, useMemo, useState } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function BehaviorHistory({ auth, patientId }) {
  const { baseUrl, token, authorized, registryVersion, registryHash } = auth;
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

useEffect(() => {
  if (!authorized || !patientId || !baseUrl) return;

  const ac = new AbortController();

  setLoading(true);
  setErr(null);
  setPayload(null);

  const url = `${baseUrl}/console/patients/${encodeURIComponent(patientId)}/nudges`;

  fetch(url, {
    signal: ac.signal,
    headers: buildKeyrdHeaders({
      token,
      registryVersion,
      registryHash,
      extra: { Accept: "application/json" },
    }),
  })
    .then(async (res) => {
      const text = await res.text();
      let json;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        throw new Error(`Non-JSON response: ${text.slice(0, 120)}…`);
      }

      if (!res.ok || !json?.ok) {
        const msg = json?.error || json?.reason || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      setPayload(json);
    })
    .catch((e) => {
      if (e?.name === "AbortError") return;
      setErr(e?.message || String(e));
    })
    .finally(() => {
      if (!ac.signal.aborted) setLoading(false);
    });

  return () => ac.abort();
}, [authorized, baseUrl, token, patientId, registryVersion, registryHash]);

  const rows = useMemo(() => {
    if (!payload) return [];
    const nudges = Array.isArray(payload.nudges) ? payload.nudges : [];
    const feedback = Array.isArray(payload.feedback) ? payload.feedback : [];

    const fbByNudgeId = {};
    for (const fb of feedback) {
      if (fb && fb.nudge_log_id != null) {
        fbByNudgeId[fb.nudge_log_id] = fb;
      }
    }

    return nudges.map((n) => {
      const fb = fbByNudgeId[n.id];
      return {
        id: n.id,
        sent_at: n.sent_at || n.timestamp_utc || n.created_at || null,
        action_id: n.action_id ?? n.nudge_id ?? null,
        goal: n.goal || null,
        message: n.message || null,
        feedback_response: fb?.response || fb?.response_type || null,
        feedback_reward:
          typeof fb?.reward === "number" ? fb.reward : null,
        feedback_at: fb?.timestamp_utc || fb?.created_at || null,
      };
    });
  }, [payload]);

  const policySummary = useMemo(() => {
    const pm = payload?.policy_metrics;
    if (!pm || typeof pm !== "object") return null;
    const summary = pm.summary || {};
    return {
      total_nudges: summary.total_nudges ?? null,
      total_feedback: summary.total_feedback ?? null,
      mean_reward: summary.mean_reward ?? null,
      first_nudge_at: summary.first_nudge_at ?? null,
      last_nudge_at: summary.last_nudge_at ?? null,
      first_feedback_at: summary.first_feedback_at ?? null,
      last_feedback_at: summary.last_feedback_at ?? null,
    };
  }, [payload]);

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
        Behavior History
      </h1>
      <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 16 }}>
        Per-nudge timeline combining delivery, latest feedback, and RL
        policy metrics. Powered by{" "}
        <code>/console/patients/&lt;patient_id&gt;/nudges</code>.
      </p>

      {!patientId && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px dashed #9ca3af",
            backgroundColor: "#f9fafb",
            fontSize: 13,
          }}
        >
          No patient selected. Choose or paste a{" "}
          <code>patient_id</code> in the Patient List panel.
        </div>
      )}

      {patientId && !authorized && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            fontSize: 12,
          }}
        >
          Paste a bearer token in the top bar to load behavior history.
        </div>
      )}

      {authorized && patientId && loading && <div>Loading history…</div>}

      {authorized && patientId && err && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
          }}
        >
          Error loading history: {err}
        </div>
      )}

      {authorized &&
        patientId &&
        !loading &&
        !err &&
        policySummary && (
          <div
            style={{
              marginTop: 12,
              marginBottom: 12,
              padding: 12,
              borderRadius: 10,
              backgroundColor: "#ffffff",
              boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
              border: "1px solid #e5e7eb",
              fontSize: 12,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 6,
              }}
            >
              RL Policy Summary
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(130px, 1fr))",
                gap: 8,
              }}
            >
              <SummaryItem
                label="Total nudges"
                value={policySummary.total_nudges}
              />
              <SummaryItem
                label="Total feedback"
                value={policySummary.total_feedback}
              />
              <SummaryItem
                label="Mean reward"
                value={
                  policySummary.mean_reward != null
                    ? policySummary.mean_reward.toFixed(3)
                    : "—"
                }
              />
              <SummaryItem
                label="First nudge"
                value={policySummary.first_nudge_at || "—"}
              />
              <SummaryItem
                label="Last nudge"
                value={policySummary.last_nudge_at || "—"}
              />
              <SummaryItem
                label="First feedback"
                value={policySummary.first_feedback_at || "—"}
              />
              <SummaryItem
                label="Last feedback"
                value={policySummary.last_feedback_at || "—"}
              />
            </div>
          </div>
        )}

      {authorized && patientId && !loading && !err && rows.length > 0 && (
        <table
          style={{
            width: "100%",
            marginTop: 12,
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <th style={{ padding: "6px 4px" }}>Sent</th>
              <th style={{ padding: "6px 4px" }}>Action</th>
              <th style={{ padding: "6px 4px" }}>Goal</th>
              <th style={{ padding: "6px 4px" }}>Message</th>
              <th style={{ padding: "6px 4px" }}>Feedback</th>
              <th style={{ padding: "6px 4px" }}>Reward</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                style={{ borderBottom: "1px solid #f3f4f6" }}
              >
                <td style={{ padding: "4px 4px", whiteSpace: "nowrap" }}>
                  {r.sent_at || "—"}
                </td>
                <td style={{ padding: "4px 4px" }}>
                  {r.action_id != null ? r.action_id : "—"}
                </td>
                <td style={{ padding: "4px 4px" }}>
                  {r.goal || "—"}
                </td>
                <td
                  style={{
                    padding: "4px 4px",
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={r.message || ""}
                >
                  {r.message || "—"}
                </td>
                <td style={{ padding: "4px 4px" }}>
                  {r.feedback_response || "—"}
                </td>
                <td style={{ padding: "4px 4px" }}>
                  {r.feedback_reward != null ? r.feedback_reward : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {authorized &&
        patientId &&
        !loading &&
        !err &&
        rows.length === 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
            No nudge history yet for this patient.
          </div>
        )}
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: "#6b7280",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default BehaviorHistory;
